/*
 * Copyright (c) 2024, King Living
 * Google Maps Component - GM-004
 */

import React, {useEffect, useRef, useState, useCallback, useMemo} from 'react';
import PropTypes from 'prop-types';
import {Box, Spinner, Text, Alert, AlertIcon} from '@chakra-ui/react';

/**
 * GoogleMap Component
 *
 * @override GM-004
 * - Loads Maps JavaScript API using official NPM js-api-loader package
 * - Accepts apiKey, center, zoom, and markers as props
 * - Renders map in referenced DOM node
 * - Provides default center for each country
 */
const GoogleMap = ({
  apiKey,
  center,
  zoom = 10,
  markers = [],
  onMapLoad,
  onMarkerClick,
  onMapClick,
  className,
  style,
  loading = 'Loading map...',
  error = 'Failed to load map',
  height = '400px',
  fitBoundsToMarkers = false,
  fitBoundsPadding = 50,
  ...props
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const googleMapsRef = useRef(null);
  const isInitializedRef = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);

  // Memoize initial center to prevent re-initialization
  const initialCenter = useMemo(() => {
    return center || {lat: -33.8688, lng: 151.2093}; // Default Sydney
  }, []); // Empty deps - only use initial value

  // Add markers to map
  const addMarkersToMap = useCallback((map, markerData, maps) => {
    if (!map || !maps || !markerData) return;

    // Clear existing markers
    markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current = [];

    markerData.forEach((markerInfo, index) => {
      // Build marker icon if custom icon is provided
      let markerIcon = null;

      if (markerInfo.icon) {
        if (typeof markerInfo.icon === 'string') {
          // Simple URL string
          markerIcon = markerInfo.icon;
        } else if (markerInfo.icon.url) {
          // Custom icon object with url, scaledSize, anchor
          markerIcon = {
            url: markerInfo.icon.url,
            scaledSize: markerInfo.icon.scaledSize ?
              new maps.Size(markerInfo.icon.scaledSize.width, markerInfo.icon.scaledSize.height) :
              null,
            anchor: markerInfo.icon.anchor ?
              new maps.Point(markerInfo.icon.anchor.x, markerInfo.icon.anchor.y) :
              null,
          };
        }
      }

      const marker = new maps.Marker({
        position: markerInfo.position,
        map: map,
        title: markerInfo.title || `Store ${index + 1}`,
        icon: markerIcon,
        animation: markerInfo.animation || maps.Animation.DROP,
      });

      // Add click listener
      if (onMarkerClick) {
        marker.addListener('click', () => {
          onMarkerClick(markerInfo, marker);
        });
      }

      // Add info window if provided
      if (markerInfo.infoWindow) {
        const infoWindow = new maps.InfoWindow({
          content: markerInfo.infoWindow,
        });

        marker.addListener('click', () => {
          infoWindow.open(map, marker);
        });
      }

      markersRef.current.push(marker);
    });
  }, [onMarkerClick]);

  // Load Google Maps API only once on mount
  useEffect(() => {
    // Only initialize once
    if (isInitializedRef.current) return;

    const loadGoogleMapsAPI = async () => {
      if (!apiKey) {
        setErrorMessage('Google Maps API key is required');
        setIsLoading(false);

        return;
      }

      // Check if API key looks like a real key (starts with AIza)
      if (!apiKey.startsWith('AIza') || apiKey.length < 30) {
        setErrorMessage('Invalid Google Maps API key. Please check your configuration.');
        setIsLoading(false);

        return;
      }

      try {
        // Dynamic import of Google Maps Loader
        const {Loader} = await import('@googlemaps/js-api-loader');

        const loader = new Loader({
          apiKey,
          version: 'weekly',
          libraries: ['places', 'geometry'],
        });

        const google = await loader.load();

        googleMapsRef.current = google.maps;

        // Initialize map
        if (mapRef.current && google.maps) {
          const map = new google.maps.Map(mapRef.current, {
            center: initialCenter,
            zoom: zoom,
            mapTypeId: google.maps.MapTypeId.ROADMAP,
            mapTypeControl: true,
            streetViewControl: true,
            fullscreenControl: true,
            zoomControl: true,
          });

          mapInstanceRef.current = map;
          isInitializedRef.current = true;

          // Add initial markers
          if (markers && markers.length > 0) {
            addMarkersToMap(map, markers, google.maps);
          }

          // Set up event listeners
          if (onMapLoad) {
            onMapLoad(map);
          }

          if (onMapClick) {
            map.addListener('click', (event) => {
              onMapClick(event.latLng);
            });
          }

          setIsLoading(false);
        }
      } catch (err) {
        console.error('Failed to load Google Maps:', err);
        setErrorMessage(error);
        setIsLoading(false);
      }
    };

    loadGoogleMapsAPI();
  }, [apiKey]); // Only depend on apiKey - load once

  // Update map center when center prop changes (without reloading)
  useEffect(() => {
    if (mapInstanceRef.current && center) {
      mapInstanceRef.current.panTo({lat: center.lat, lng: center.lng});
    }
  }, [center?.lat, center?.lng]);

  // Update zoom when zoom prop changes
  useEffect(() => {
    if (mapInstanceRef.current && zoom !== undefined) {
      const currentZoom = mapInstanceRef.current.getZoom();

      if (currentZoom !== zoom) {
        mapInstanceRef.current.setZoom(zoom);
      }
    }
  }, [zoom]);

  // Update markers when markers prop changes
  useEffect(() => {
    if (mapInstanceRef.current && googleMapsRef.current && markers) {
      addMarkersToMap(mapInstanceRef.current, markers, googleMapsRef.current);

      // Fit bounds to show all markers if enabled
      if (fitBoundsToMarkers && markers.length > 0) {
        const bounds = new googleMapsRef.current.LatLngBounds();

        markers.forEach((marker) => {
          if (marker.position) {
            bounds.extend(marker.position);
          }
        });

        // Only fit bounds if we have valid bounds
        if (!bounds.isEmpty()) {
          // If only one marker, center on it with appropriate zoom
          if (markers.length === 1) {
            mapInstanceRef.current.setCenter(markers[0].position);
            mapInstanceRef.current.setZoom(14);
          } else {
            // Multiple markers - fit to bounds with padding
            mapInstanceRef.current.fitBounds(bounds, fitBoundsPadding);
          }
        }
      }
    }
  }, [markers, addMarkersToMap, fitBoundsToMarkers, fitBoundsPadding]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      markersRef.current.forEach((marker) => marker.setMap(null));
      markersRef.current = [];
    };
  }, []);

  if (errorMessage) {
    return (
      <Box
        alignItems="center"
        bg="gray.50"
        borderRadius="lg"
        display="flex"
        flexDirection="column"
        height={height}
        justifyContent="center"
        p={6}
        textAlign="center"
        width="100%"
        {...props}
      >
        <Alert borderRadius="md" maxWidth="400px" mb={4} status="warning">
          <AlertIcon />
          <Box>
            <Text fontWeight="bold" mb={2}>Google Maps Not Available</Text>
            <Text fontSize="sm">{errorMessage}</Text>
            <Text color="gray.600" fontSize="xs" mt={2}>
              To enable Google Maps, please:
              <br />1. Get a valid API key from Google Cloud Console
              <br />2. Update GOOGLE_MAPS_API_KEY in your environment variables
              <br />3. Restart the development server
            </Text>
          </Box>
        </Alert>
      </Box>
    );
  }

  return (
    <Box height={height} position="relative" width="100%" {...props}>
      {isLoading && (
        <Box
          alignItems="center"
          bg="white"
          bottom="0"
          display="flex"
          justifyContent="center"
          left="0"
          position="absolute"
          right="0"
          top="0"
          zIndex="1"
        >
          <Spinner color="blue.500" size="lg" />
          <Text ml={3}>{loading}</Text>
        </Box>
      )}

      <Box
        ref={mapRef}
        className={className}
        height="100%"
        style={style}
        width="100%"
      />
    </Box>
  );
};

GoogleMap.propTypes = {
  apiKey: PropTypes.string.isRequired,
  center: PropTypes.shape({
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired,
  }),
  className: PropTypes.string,
  error: PropTypes.string,
  fitBoundsPadding: PropTypes.number,
  fitBoundsToMarkers: PropTypes.bool,
  height: PropTypes.string,
  loading: PropTypes.string,
  markers: PropTypes.arrayOf(
    PropTypes.shape({
      animation: PropTypes.string,
      icon: PropTypes.string,
      infoWindow: PropTypes.string,
      position: PropTypes.shape({
        lat: PropTypes.number.isRequired,
        lng: PropTypes.number.isRequired,
      }).isRequired,
      title: PropTypes.string,
    })
  ),
  onMapClick: PropTypes.func,
  onMapLoad: PropTypes.func,
  onMarkerClick: PropTypes.func,
  style: PropTypes.object,
  zoom: PropTypes.number,
};

GoogleMap.displayName = 'GoogleMap';

export default GoogleMap;
